import type { StatsAnfrage } from '../../../lib/apiStatsApi';

/**
 * Filterzustand der API-Statistik ↔ Adresszeile.
 *
 * Die Filter liegen als Query-Parameter in der URL (der Tab selbst im Hash
 * `#api`), damit sich ein Blick verlinken lässt: wer den Link öffnet, sieht
 * exakt dieselbe Auswahl. Nur vom Standard abweichende Werte werden
 * geschrieben — Standardansicht = saubere Adresse.
 */

export type Sicht = 'zeit' | 'kunden' | 'schluessel' | 'webhooks';
export type Zeitwahl = 'heute' | '7t' | '30t' | '90t' | 'monat' | 'vormonat' | 'frei';
export type Nur = 'alles' | 'fehler' | 'erfolge';

export interface FilterZustand {
  sicht: Sicht;
  zeitwahl: Zeitwahl;
  von: string; // nur bei zeitwahl 'frei' maßgeblich
  bis: string;
  mandanten: string[];
  schluessel: number[];
  endpunkt: string;
  nur: Nur;
}

/** Berlin-Kalendertag — dieselbe Tagesgrenze wie die Erfassung im Backend. */
export function heuteBerlin(wann: Date = new Date()): string {
  const teile: Record<string, string> = {};
  const format = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
  });
  for (const p of format.formatToParts(wann)) teile[p.type] = p.value;
  return `${teile.year}-${teile.month}-${teile.day}`;
}

export function tagPlus(tag: string, delta: number): string {
  const d = new Date(`${tag}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

/** Schnellwahl → konkrete Grenzen. 'frei' behält die eingegebenen Werte. */
export function zeitraumVon(zeitwahl: Zeitwahl, von: string, bis: string): { von: string; bis: string } {
  const heute = heuteBerlin();
  switch (zeitwahl) {
    case 'heute': return { von: heute, bis: heute };
    case '7t': return { von: tagPlus(heute, -6), bis: heute };
    case '30t': return { von: tagPlus(heute, -29), bis: heute };
    case '90t': return { von: tagPlus(heute, -89), bis: heute };
    case 'monat': return { von: `${heute.slice(0, 7)}-01`, bis: heute };
    case 'vormonat': {
      const ersterDiesenMonat = `${heute.slice(0, 7)}-01`;
      const letzterVormonat = tagPlus(ersterDiesenMonat, -1);
      return { von: `${letzterVormonat.slice(0, 7)}-01`, bis: letzterVormonat };
    }
    case 'frei': return { von: von || tagPlus(heute, -29), bis: bis || heute };
  }
}

export const STANDARD: FilterZustand = {
  sicht: 'kunden', // Priorität des Betreibers: „Wer nutzt wie viel" zuerst
  zeitwahl: '30t',
  von: '', bis: '',
  mandanten: [], schluessel: [], endpunkt: '', nur: 'alles',
};

const TAG = /^\d{4}-\d{2}-\d{2}$/;

export function ausParams(p: URLSearchParams): FilterZustand {
  const sicht = p.get('sicht');
  const zeitwahl = p.get('zeitraum');
  const nur = p.get('nur');
  return {
    sicht: sicht === 'zeit' || sicht === 'schluessel' || sicht === 'webhooks' ? sicht : 'kunden',
    zeitwahl: (['heute', '7t', '30t', '90t', 'monat', 'vormonat', 'frei'] as Zeitwahl[]).includes(zeitwahl as Zeitwahl)
      ? (zeitwahl as Zeitwahl) : '30t',
    von: TAG.test(p.get('von') ?? '') ? (p.get('von') as string) : '',
    bis: TAG.test(p.get('bis') ?? '') ? (p.get('bis') as string) : '',
    mandanten: (p.get('mandanten') ?? '').split(',').filter((m) => m === 'ohne' || /^[tu]\d+$/.test(m)),
    schluessel: (p.get('schluessel') ?? '').split(',').map(Number).filter((n) => Number.isInteger(n) && n > 0),
    endpunkt: p.get('endpunkt') ?? '',
    nur: nur === 'fehler' || nur === 'erfolge' ? nur : 'alles',
  };
}

export function zuParams(f: FilterZustand): URLSearchParams {
  const p = new URLSearchParams();
  if (f.sicht !== STANDARD.sicht) p.set('sicht', f.sicht);
  if (f.zeitwahl !== STANDARD.zeitwahl) p.set('zeitraum', f.zeitwahl);
  if (f.zeitwahl === 'frei') {
    if (f.von) p.set('von', f.von);
    if (f.bis) p.set('bis', f.bis);
  }
  if (f.mandanten.length > 0) p.set('mandanten', f.mandanten.join(','));
  if (f.schluessel.length > 0) p.set('schluessel', f.schluessel.join(','));
  if (f.endpunkt) p.set('endpunkt', f.endpunkt);
  if (f.nur !== 'alles') p.set('nur', f.nur);
  return p;
}

/** Zustand → Anfrage an die Auswertungs-Endpunkte (mit aufgelöstem Zeitraum). */
export function alsAnfrage(f: FilterZustand): StatsAnfrage {
  const { von, bis } = zeitraumVon(f.zeitwahl, f.von, f.bis);
  return { von, bis, mandanten: f.mandanten, schluessel: f.schluessel, endpunkt: f.endpunkt.trim(), nur: f.nur };
}
